import { useState, useMemo, useCallback } from 'react';
import { useLeaguesForResultsQuery, useGameResultsQuery } from './useGameResultsQuery';
import type { GameResult, FilterType, DateSortType } from '../types/gameResults.types';
import type { LeagueOption } from '../helpers/gameResultsApi';
import type { WeekRange } from '../helpers/dateRange.helper';
import { LEAGUE_DISPLAY_PRIORITY } from '../../create/constants/create.constants';
import {
  FILTER_TYPES,
  DATE_SORT_TYPES,
  FILTER_KEYS,
  VIEW_OPTION_KEYS,
  DEFAULT_LEAGUE_NAME,
  type ViewOptionKey,
} from '../constants/gameResults.constants';

interface UseGameResultsReturn {
  gameResults: GameResult[];
  filteredResults: GameResult[];
  isLoading: boolean;
  /** True when in league view but default league not resolved yet (leagues loading). Avoids showing date-view data then jumping to league. */
  isLeagueViewPending: boolean;
  /** True when showing keepPreviousData from a different query (e.g. date data while league is loading). Used to avoid flash of wrong content in league view. */
  isPlaceholderData: boolean;
  /** True when a new request is in flight but we still have data (placeholderData). Show a subtle loading indicator. */
  isFetchingWithData: boolean;
  isError: boolean;
  refetchResults: () => Promise<unknown>;
  filterType: FilterType;
  dateSortType: DateSortType;
  selectedLeague: string;
  availableLeagues: LeagueOption[];
  setFilterType: (filter: FilterType) => void;
  setDateSortType: (sort: DateSortType) => void;
  setSelectedLeague: (leagueId: string) => void;
  handleFilterChange: (filter: typeof FILTER_KEYS.DATE | typeof FILTER_KEYS.LEAGUE) => void;
  handleDateSortToggle: () => void;
  activeViewOption: ViewOptionKey;
  handleViewOptionSelect: (option: ViewOptionKey) => void;
}

export interface UseGameResultsOptions {
  /** When in date view: selected calendar day YYYY-MM-DD (for display filter). */
  selectedDate?: string;
  /** When in date view: week bounds from getWeekRangeForDate(selectedDate) for API and query key. */
  range?: WeekRange | null;
  /** When in date view: IANA timezone from Intl.DateTimeFormat().resolvedOptions().timeZone. */
  timezone?: string | null;
}

export type GameResultsViewMode = 'date' | 'league';

/** Sort leagues by display priority (Premier League first, etc.). */
function sortLeaguesByPriority(leagues: LeagueOption[]): LeagueOption[] {
  const PRIORITY_LAST = 9999;
  return [...leagues].sort((a, b) => {
    const aKey = a.name.trim().toLowerCase();
    const bKey = b.name.trim().toLowerCase();
    const aIdx = LEAGUE_DISPLAY_PRIORITY.indexOf(aKey);
    const bIdx = LEAGUE_DISPLAY_PRIORITY.indexOf(bKey);
    const aPri = aIdx === -1 ? PRIORITY_LAST : aIdx;
    const bPri = bIdx === -1 ? PRIORITY_LAST : bIdx;
    if (aPri !== bPri) return aPri - bPri;
    return aKey.localeCompare(bKey);
  });
}

export const useGameResults = (
  options?: UseGameResultsOptions,
  viewMode: GameResultsViewMode = 'date'
): UseGameResultsReturn => {
  const { range, timezone } = options ?? {};
  const [filterType, setFilterType] = useState<FilterType>(FILTER_TYPES.DATE);
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null);
  const [dateSortType, setDateSortType] = useState<DateSortType>(DATE_SORT_TYPES.NEWEST);

  const { data: availableLeagues = [] } = useLeaguesForResultsQuery();

  const sortedLeagues = useMemo(() => sortLeaguesByPriority(availableLeagues), [availableLeagues]);

  const defaultLeagueId = useMemo(() => {
    const premier = sortedLeagues.find(
      (l) => l.name.trim().toLowerCase() === DEFAULT_LEAGUE_NAME.toLowerCase()
    );
    return premier?.id ?? sortedLeagues[0]?.id ?? null;
  }, [sortedLeagues]);

  const effectiveFilterType: FilterType =
    viewMode === 'league'
      ? FILTER_TYPES.LEAGUE
      : dateSortType === DATE_SORT_TYPES.NEWEST
        ? FILTER_TYPES.DATE_DESC
        : FILTER_TYPES.DATE_ASC;

  const resolvedLeagueId = viewMode === 'league'
    ? (selectedLeague ?? defaultLeagueId)
    : null;

  /** When true, we are in league view but do not have a league yet (leagues loading). Show loading + empty to avoid flashing date-view data. */
  const isLeagueViewPending = viewMode === 'league' && resolvedLeagueId == null;

  const leagueQueryEnabled = viewMode !== 'league' || resolvedLeagueId != null;

  const {
    data: gameResults = [],
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    refetch: refetchResults,
  } = useGameResultsQuery(
    effectiveFilterType,
    resolvedLeagueId ?? 'all',
    dateSortType,
    range ?? null,
    timezone ?? null,
    leagueQueryEnabled
  );

  const filteredResults = useMemo(() => gameResults, [gameResults]);
  const isFetchingWithData = isFetching && gameResults.length > 0;

  const handleFilterChange = useCallback((filter: typeof FILTER_KEYS.DATE | typeof FILTER_KEYS.LEAGUE) => {
    if (filter === FILTER_KEYS.DATE) {
      setFilterType(dateSortType === DATE_SORT_TYPES.NEWEST ? FILTER_TYPES.DATE_DESC : FILTER_TYPES.DATE_ASC);
    } else {
      setFilterType(FILTER_TYPES.LEAGUE);
    }
  }, [dateSortType]);

  const handleDateSortToggle = useCallback(() => {
    const newSortType = dateSortType === DATE_SORT_TYPES.NEWEST ? DATE_SORT_TYPES.OLDEST : DATE_SORT_TYPES.NEWEST;
    setDateSortType(newSortType);
    setFilterType(newSortType === DATE_SORT_TYPES.NEWEST ? FILTER_TYPES.DATE_DESC : FILTER_TYPES.DATE_ASC);
  }, [dateSortType]);

  const activeViewOption: ViewOptionKey = useMemo(() => {
    if (filterType === FILTER_TYPES.LEAGUE) return VIEW_OPTION_KEYS.LEAGUE;
    return dateSortType === DATE_SORT_TYPES.NEWEST ? VIEW_OPTION_KEYS.DATE_NEWEST : VIEW_OPTION_KEYS.DATE_OLDEST;
  }, [filterType, dateSortType]);

  const handleViewOptionSelect = useCallback((option: ViewOptionKey) => {
    if (option === VIEW_OPTION_KEYS.LEAGUE) {
      setFilterType(FILTER_TYPES.LEAGUE);
      return;
    }
    const sortType = option === VIEW_OPTION_KEYS.DATE_NEWEST ? DATE_SORT_TYPES.NEWEST : DATE_SORT_TYPES.OLDEST;
    setDateSortType(sortType);
    setFilterType(sortType === DATE_SORT_TYPES.NEWEST ? FILTER_TYPES.DATE_DESC : FILTER_TYPES.DATE_ASC);
  }, []);

  const setLeague = useCallback((id: string) => {
    setSelectedLeague(id);
  }, []);

  return {
    gameResults,
    filteredResults,
    isLoading,
    isLeagueViewPending,
    isPlaceholderData: isPlaceholderData ?? false,
    isFetchingWithData,
    isError: leagueQueryEnabled && (isError ?? false),
    refetchResults,
    filterType,
    dateSortType,
    selectedLeague: resolvedLeagueId ?? defaultLeagueId ?? '',
    availableLeagues: sortedLeagues,
    setFilterType,
    setDateSortType,
    setSelectedLeague: setLeague,
    handleFilterChange,
    handleDateSortToggle,
    activeViewOption,
    handleViewOptionSelect,
  };
};
